import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { X, CheckCircle, AlertTriangle, AlertCircle, Info } from 'lucide-react';

type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface ToastData {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
  duration: number;
}

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
}

export interface ToastApi {
  toast: (options: ToastOptions) => void;
}

type ToastContextValue = ToastApi;

const ToastContext = createContext<ToastContextValue | null>(null);

const icons: Record<ToastVariant, typeof CheckCircle> = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const iconColors: Record<ToastVariant, string> = {
  success: 'text-green-500',
  error: 'text-danger-500',
  warning: 'text-warning-500',
  info: 'text-info-500',
};

const borderColors: Record<ToastVariant, string> = {
  success: 'border-l-green-500',
  error: 'border-l-danger-500',
  warning: 'border-l-warning-500',
  info: 'border-l-info-500',
};

let toastCounter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const toast = useCallback(({ title, description, variant = 'info', duration = 4000 }: ToastOptions) => {
    const id = `toast-${++toastCounter}`;
    const newToast: ToastData = { id, title, description, variant, duration };

    setToasts((prev) => [...prev, newToast]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {/* Toast container */}
      <div
        role="region"
        aria-label="Notifications"
        aria-live="polite"
        className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none"
      >
        {toasts.map((t) => {
          const Icon = icons[t.variant];
          const isAssertive = t.variant === 'error' || t.variant === 'warning';
          return (
            <div
              key={t.id}
              role={isAssertive ? 'alert' : 'status'}
              aria-live={isAssertive ? 'assertive' : 'polite'}
              className={`pointer-events-auto bg-white rounded-lg shadow-lg border border-surface-200 border-l-4 ${borderColors[t.variant]} p-4 flex gap-3 animate-in slide-in-from-right-5`}
            >
              <Icon aria-hidden="true" className={`w-5 h-5 flex-shrink-0 mt-0.5 ${iconColors[t.variant]}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-surface-900">{t.title}</p>
                {t.description && (
                  <p className="mt-1 text-sm text-surface-500">{t.description}</p>
                )}
              </div>
              <button
                type="button"
                aria-label="Dismiss notification"
                onClick={() => dismiss(t.id)}
                className="flex-shrink-0 text-surface-400 hover:text-surface-600"
              >
                <X aria-hidden="true" className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
